import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";

function Contact() {
  const { t } = useTranslation();
  const { isLoggedIn, userName, userEmail } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setFormData((prev) => ({
        ...prev,
        name: "",
        email: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      name: prev.name || userName || "",
      email: prev.email || userEmail || "",
    }));
  }, [isLoggedIn, userName, userEmail]);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const isFormComplete = Object.values(formData).every((value) => value.trim());
    if (!isFormComplete) {
      setStatusType("error");
      setStatusMessage(t("contact.fillAllFields"));
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email.trim())) {
      setStatusType("error");
      setStatusMessage(t("contact.validEmail"));
      return;
    }

    try {
      setIsSubmitting(true);
      setStatusType("");
      setStatusMessage("");

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      };

      const response = await fetch("https://formsubmit.co/ajax/contact@dblogcss.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Unable to send message");
      }

      setStatusType("success");
      setStatusMessage(t("contact.messageSent"));
      setFormData({
        name: isLoggedIn ? userName || "" : "",
        email: isLoggedIn ? userEmail || "" : "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setStatusType("error");
      setStatusMessage(t("contact.sendFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="container contact-inner">
        <div className="contact-header">
          <p className="contact-eyebrow">{t("contact.eyebrow")}</p>
          <h2 className="main-title">{t("contact.title")}</h2>
          <p className="section-lead">{t("contact.lead")}</p>
        </div>

        <div className="contact-layout">
          <aside className="contact-aside">
            <p className="contact-aside-title">{t("contact.asideTitle")}</p>
            <p className="contact-aside-copy">{t("contact.asideCopy")}</p>
            <ul className="contact-aside-list">
              <li>
                <i className="fas fa-envelope" aria-hidden="true"></i>
                <a href="mailto:info@dblogcss.com">{t("footer.email")}</a>
              </li>
              <li>
                <i className="fas fa-phone-alt" aria-hidden="true"></i>
                <a href="tel:+251112132993">{t("footer.mobile")}</a>
              </li>
              <li>
                <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                <span>
                  {t("footer.addressLine1")}
                  <br />
                  {t("footer.addressLine2")}
                </span>
              </li>
            </ul>
          </aside>

          <form className="contact-form" onSubmit={submitHandler} noValidate>
            <div className="contact-fields">
              <label className="contact-field">
                <span>{t("contact.namePlaceholder")}</span>
                <input
                  name="name"
                  className="contact-input"
                  value={formData.name}
                  onChange={changeHandler}
                  autoComplete="name"
                />
              </label>

              <label className="contact-field">
                <span>{t("contact.emailPlaceholder")}</span>
                <input
                  type="email"
                  name="email"
                  className="contact-input"
                  value={formData.email}
                  onChange={changeHandler}
                  autoComplete="email"
                />
              </label>

              <label className="contact-field contact-field--full">
                <span>{t("contact.subjectPlaceholder")}</span>
                <input
                  name="subject"
                  className="contact-input"
                  value={formData.subject}
                  onChange={changeHandler}
                  autoComplete="off"
                />
              </label>

              <label className="contact-field contact-field--full">
                <span>{t("contact.messagePlaceholder")}</span>
                <textarea
                  name="message"
                  className="contact-textarea"
                  value={formData.message}
                  onChange={changeHandler}
                  rows={5}
                />
              </label>
            </div>

            <button className="form-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("contact.sending") : t("contact.sendMessage")}
            </button>

            {statusMessage && (
              <p
                className={`contact-status ${
                  statusType === "success" ? "is-success" : "is-error"
                }`}
                role="status"
              >
                {statusMessage}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
